import {
  Home,
  Box,
  Zap,
  Truck,
  Package,
  ChevronRight,
  Activity,
  Layers,
  Settings,
  Settings2,
  Database,
  Building,
  Server,
  MapPin,
  Building2,
  Radio,
  Users,
  ShieldCheck,
  FileKey,
  FileCode2,
  Tags,
  Shapes,
  Workflow,
  Rss,
  MailPlus,
  Send,
} from "lucide-react";
import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import conexIcon from "@/assets/conex-icon.png";
import farmgridLogo from "@/assets/farmgrid-logomark.png";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const organizationsItems = [
  {
    title: "Organizations",
    url: "/organizations",
    icon: Building,
  },
];

const usersItems = [
  {
    title: "Users",
    url: "/users",
    icon: Users,
  },
  {
    title: "Invitations",
    url: "/invitations",
    icon: Send,
  },
  {
    title: "Invitation Types",
    url: "/invitation-types",
    icon: MailPlus,
  },
];

const sensoryItems = [
  {
    title: "Devices",
    url: "/sensory/devices",
    icon: Zap,
  },
  {
    title: "Device Profiles",
    url: "/sensory/device-profiles",
    icon: Layers,
  },
  {
    title: "Device Types",
    url: "/sensory/device-types",
    icon: Box,
  },
  {
    title: "Manufacturers",
    url: "/sensory/manufacturers",
    icon: Package,
  },
  {
    title: "Distributors",
    url: "/sensory/distributors",
    icon: Truck,
  },
  {
    title: "Device Management",
    url: "/sensory/device-management",
    icon: Settings,
  },
];

const sensnetItems = [
  {
    title: "Network Corridors",
    url: "/sensnet/network-corridors",
    icon: MapPin,
  },
  {
    title: "Network Sites",
    url: "/sensnet/network-sites",
    icon: Building2,
  },
  {
    title: "Sensing Stations",
    url: "/sensnet/sensing-stations",
    icon: Radio,
  },
];

const eventsItems = [
  {
    title: "Event Types",
    url: "/events/event-types",
    icon: Tags,
  },
  {
    title: "Entity Types",
    url: "/events/entity-types",
    icon: Shapes,
  },
  {
    title: "Actions",
    url: "/events/actions",
    icon: Workflow,
  },
];

const remaItems = [
  {
    title: "Resource Types",
    url: "/rema/resource-types",
    icon: Settings2,
  },
  {
    title: "Resource Instances",
    url: "/rema/resource-instances",
    icon: Database,
  },
  {
    title: "Roles",
    url: "/rema/roles",
    icon: ShieldCheck,
  },
  {
    title: "Groups",
    url: "/rema/groups",
    icon: Users,
  },
  {
    title: "Policies",
    url: "/rema/policies",
    icon: FileKey,
  },
];

export function SideNavComponent() {
  const [isOrganizationsOpen, setIsOrganizationsOpen] = useState(false);
  const [isUsersOpen, setIsUsersOpen] = useState(false);
  const [isSensoryOpen, setIsSensoryOpen] = useState(true);
  const [isSensnetOpen, setIsSensnetOpen] = useState(false);
  const [isEventsOpen, setIsEventsOpen] = useState(false);
  const [isRemaOpen, setIsRemaOpen] = useState(false);
  const [isDeveloperOpen, setIsDeveloperOpen] = useState(false);
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const isActive = (url: string) => {
    return currentPath === url || currentPath.startsWith(url + "/");
  };

  return (
    // TODO: Change collapsible="offcanvas" to collapsible="icon" for icon-only collapsed mode
    // When using "icon" mode, uncomment the C360 label below for collapsed state
    <Sidebar
      variant="sidebar"
      collapsible="offcanvas"
      className="border-r bg-sidebar pt-16 mt-0"
    >
      {/* Header removed/hidden as per overlapping request */}
      <SidebarContent className="p-3 [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:transparent_transparent] hover:[scrollbar-color:color-mix(in_oklch,var(--sidebar-foreground)_25%,transparent)_transparent]">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-2">
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  size="lg"
                  tooltip="Dashboard"
                  isActive={isActive('/home')}
                  className="font-semibold w-full h-11 rounded-lg transition-all duration-200 data-[active=true]:text-primary data-[active=true]:bg-sidebar-accent/60"
                >
                  <Link to="/home">
                    <Home className="size-5 text-primary" />
                    <span className="text-base font-bold">Dashboard</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <Collapsible
                open={isOrganizationsOpen}
                onOpenChange={setIsOrganizationsOpen}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      tooltip="Organizations"
                      className="font-semibold w-full h-11 rounded-lg transition-all duration-200"
                    >
                      <Building className="size-5 text-trilogy-blue" />
                      <span className="text-base font-bold">Organizations</span>
                      <ChevronRight className="ml-auto size-4 transition-transform duration-250 ease-spring group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-0 border-l-2 border-sidebar-border/30 px-0 py-2">
                      {organizationsItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton
                            asChild
                            size="md"
                            isActive={isActive(item.url)}
                            className="ml-3 rounded-lg hover:bg-sidebar-accent/60 data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:bg-transparent"
                          >
                            <Link to={item.url}>
                              <item.icon className="size-4" />
                              <span className="text-sm">{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <Collapsible
                open={isUsersOpen}
                onOpenChange={setIsUsersOpen}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      tooltip="Users"
                      className="font-semibold w-full h-11 rounded-lg transition-all duration-200"
                    >
                      <Users className="size-5 text-trilogy-navy" />
                      <span className="text-base font-bold">Users</span>
                      <ChevronRight className="ml-auto size-4 transition-transform duration-250 ease-spring group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-0 border-l-2 border-sidebar-border/30 px-0 py-2">
                      {usersItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton
                            asChild
                            size="md"
                            isActive={isActive(item.url)}
                            className="ml-3 rounded-lg hover:bg-sidebar-accent/60 data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:bg-transparent"
                          >
                            <Link to={item.url}>
                              <item.icon className="size-4" />
                              <span className="text-sm">{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <Collapsible
                open={isSensoryOpen}
                onOpenChange={setIsSensoryOpen}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      tooltip="Sensory"
                      className="font-semibold w-full h-11 rounded-lg transition-all duration-200"
                    >
                      <Activity className="size-5 text-accent-vibrant" />
                      <span className="text-base font-bold">Sensory</span>
                      <ChevronRight className="ml-auto size-4 transition-transform duration-250 ease-spring group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-0 border-l-2 border-sidebar-border/30 px-0 py-2">
                      {sensoryItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton
                            asChild
                            size="md"
                            isActive={isActive(item.url)}
                            className="ml-3 rounded-lg hover:bg-sidebar-accent/60 data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:bg-transparent"
                          >
                            <Link to={item.url}>
                              <item.icon className="size-4" />
                              <span className="text-sm">{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <Collapsible
                open={isSensnetOpen}
                onOpenChange={setIsSensnetOpen}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      tooltip="Sensing Networks"
                      className="font-semibold w-full h-11 rounded-lg transition-all duration-200"
                    >
                      <Radio className="size-5 text-trilogy-yellow" />
                      <span className="text-base font-bold">Sensing Networks</span>
                      <ChevronRight className="ml-auto size-4 transition-transform duration-250 ease-spring group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-0 border-l-2 border-sidebar-border/30 px-0 py-2">
                      {sensnetItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton
                            asChild
                            size="md"
                            isActive={isActive(item.url)}
                            className="ml-3 rounded-lg hover:bg-sidebar-accent/60 data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:bg-transparent"
                          >
                            <Link to={item.url}>
                              <item.icon className="size-4" />
                              <span className="text-sm">{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <Collapsible
                open={isEventsOpen}
                onOpenChange={setIsEventsOpen}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      tooltip="Events"
                      className="font-semibold w-full h-11 rounded-lg transition-all duration-200"
                    >
                      <Rss className="size-5 text-trilogy-yellow" />
                      <span className="text-base font-bold">Events</span>
                      <ChevronRight className="ml-auto size-4 transition-transform duration-250 ease-spring group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-0 border-l-2 border-sidebar-border/30 px-0 py-2">
                      {eventsItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton
                            asChild
                            size="md"
                            isActive={isActive(item.url)}
                            className="ml-3 rounded-lg hover:bg-sidebar-accent/60 data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:bg-transparent"
                          >
                            <Link to={item.url}>
                              <item.icon className="size-4" />
                              <span className="text-sm">{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <Collapsible
                open={isRemaOpen}
                onOpenChange={setIsRemaOpen}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      tooltip="Resource Management"
                      className="font-semibold w-full h-11 rounded-lg transition-all duration-200"
                    >
                      <Server className="size-5 text-trilogy-red" />
                      <span className="text-base font-bold">Resource Management</span>
                      <ChevronRight className="ml-auto size-4 transition-transform duration-250 ease-spring group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-0 border-l-2 border-sidebar-border/30 px-0 py-2">
                      {remaItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton
                            asChild
                            size="md"
                            isActive={isActive(item.url)}
                            className="ml-3 rounded-lg hover:bg-sidebar-accent/60 data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:bg-transparent"
                          >
                            <Link to={item.url}>
                              <item.icon className="size-4" />
                              <span className="text-sm">{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <Collapsible
                open={isDeveloperOpen}
                onOpenChange={setIsDeveloperOpen}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      tooltip="API Docs"
                      className="font-semibold w-full h-11 rounded-lg transition-all duration-200"
                    >
                      <FileCode2 className="size-5 text-primary" />
                      <span className="text-base font-bold">API Docs</span>
                      <ChevronRight className="ml-auto size-4 transition-transform duration-250 ease-spring group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-0 border-l-2 border-sidebar-border/30 px-0 py-2">
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          size="md"
                          isActive={isActive('/api-docs/conex')}
                          className="ml-3 rounded-lg hover:bg-sidebar-accent/60 data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:bg-transparent"
                        >
                          <Link to="/api-docs/conex">
                            <img src={conexIcon} alt="" className="size-4 object-contain" />
                            <span className="text-sm">ConEx API</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          size="md"
                          isActive={isActive('/api-docs/farmgrid')}
                          className="ml-3 rounded-lg hover:bg-sidebar-accent/60 data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:bg-transparent"
                        >
                          <Link to="/api-docs/farmgrid">
                            <img src={farmgridLogo} alt="" className="size-4 object-contain" />
                            <span className="text-sm">FarmGrid API</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}

